import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#restorePersistedError();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restorePersistedError();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #getDatoraClient() {
    if (window.co2 && typeof window.co2.removeDiscountCode === 'function') {
      return window.co2;
    }

    return typeof co2 !== 'undefined' ? co2 : null;
  }

  #getPersistedErrors() {
    const errors = window.__cartDiscountErrors;
    return errors && typeof errors === 'object' ? errors : (window.__cartDiscountErrors = {});
  }

  #clearPersistedError() {
    if (typeof this.dataset.sectionId !== 'string') return;

    delete this.#getPersistedErrors()[this.dataset.sectionId];
  }

  /**
   * Persists the current discount error long enough to survive a drawer re-render.
   *
   * @param {'discount_code' | 'shipping'} type
   */
  #persistError(type) {
    if (typeof this.dataset.sectionId !== 'string') return;

    this.#getPersistedErrors()[this.dataset.sectionId] = {
      type,
      expiresAt: Date.now() + 15000,
    };
  }

  #restorePersistedError() {
    if (typeof this.dataset.sectionId !== 'string') return;

    const persistedError = this.#getPersistedErrors()[this.dataset.sectionId];
    if (!persistedError) return;

    if (persistedError.expiresAt <= Date.now()) {
      this.#clearPersistedError();
      return;
    }

    this.#handleDiscountError(persistedError.type, { persist: false });
  }

  /**
   * Normalizes Datora apply results to determine whether the code was rejected.
   *
   * @param {unknown} result
   * @returns {boolean}
   */
  #isRejectedDiscountResult(result) {
    if (result === false || result == null) return true;
    if (typeof result !== 'object') return false;

    const value = /** @type {Record<string, unknown>} */ (result);

    if (value.applied === false || value.success === false || value.ok === false) {
      return true;
    }

    if (typeof value.status === 'string' && ['error', 'failed', 'invalid'].includes(value.status.toLowerCase())) {
      return true;
    }

    if (Array.isArray(value.errors) && value.errors.length > 0) {
      return true;
    }

    return Boolean(value.error);
  }

  /**
   * Parses the section HTML and returns the applied discount codes rendered in the UI.
   *
   * @param {string} html
   * @returns {string[]}
   */
  #extractDiscountCodesFromSectionHtml(html) {
    if (typeof this.dataset.sectionId !== 'string') return [];

    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];

    return Array.from(discountCodes)
      .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
      .filter((code) => typeof code === 'string');
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      this.#clearPersistedError();
      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const datoraClient = this.#getDatoraClient();
      if (datoraClient?.setDiscountCode) {
        const applied = await datoraClient.setDiscountCode(discountCodeValue);
        if (this.#isRejectedDiscountResult(applied)) {
          discountCode.value = '';
          this.#handleDiscountError('discount_code');
          return;
        }

        const sectionHtml = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false);
        const renderedDiscountCodes = this.#extractDiscountCodesFromSectionHtml(sectionHtml);
        const submittedCode = discountCodeValue.trim().toLowerCase();
        const renderedCode = renderedDiscountCodes.some((code) => code.trim().toLowerCase() === submittedCode);

        if (!renderedCode) {
          discountCode.value = '';
          this.#handleDiscountError('discount_code');
          return;
        }

        morphSection(this.dataset.sectionId, sectionHtml);
        this.#clearPersistedError();
        discountCode.value = '';
        return;
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
      this.#clearPersistedError();
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Failed to apply cart discount code', error);
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof Element) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const datoraClient = this.#getDatoraClient();
      if (datoraClient?.removeDiscountCode) {
        await datoraClient.removeDiscountCode();
        await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false, mode: 'full' });
        return;
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Failed to remove cart discount code', error);
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   * @param {{ persist?: boolean }} [options]
   */
  #handleDiscountError(type, options = {}) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    const { persist = true } = options;

    if (persist) {
      this.#persistError(type);
    }

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
